// Manual sections 6–8: campaigns, scorecards, and knowledge-base files — the setup that
// everything else depends on. See ../index.js for the schema.

const setup = [
  {
    id: 'campaigns',
    group: 'Set up',
    title: 'Campaigns',
    lede: 'Group recordings and link the scorecards they are assessed against.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Find them under **Quality › Campaigns**. The list shows each campaign’s linked scorecards and whether it is **Active** or **Inactive**. Every call in a campaign is assessed against every scorecard linked to it.',
      },
      { type: 'h3', text: 'Creating a campaign' },
      {
        type: 'steps',
        items: [
          {
            title: 'Choose `New Campaign`.',
            text:
              'Enter a **Campaign Name** that makes its purpose obvious. Names must be unique, and **cannot be changed later**.',
          },
          {
            title: 'Add knowledge-base files, if you have them.',
            text:
              'Use `Upload examples` to attach reference documents the AI should score against. See {{Knowledge base|#knowledge-base}}.',
          },
          {
            title: 'Link its scorecards.',
            text:
              'Pick existing scorecards from the **Scorecards** dropdown. To build one on the spot, choose `Create New Scorecard`, give it a **Scorecard Name** and add its questions with `Add Question`. An inline scorecard name must not already be in use.',
          },
          {
            title: 'Choose `Save`, then `Confirm Save`.',
            text: 'The campaign can now be picked when you upload recordings or set up jobs.',
          },
        ],
      },
      {
        type: 'note',
        variant: 'tip',
        tag: 'Inline scorecards',
        text:
          'A scorecard created inside a campaign has only the basics: name, question text, criteria, type, weight and priority. Open it from **Quality › Scorecards** afterwards to set its language, scoring system and the rest.',
      },
      { type: 'h3', text: 'Managing a campaign afterwards' },
      {
        type: 'ul',
        items: [
          '`View` opens a read-only page: the linked scorecards with their question counts and languages, the total number of questions, the status, and when the campaign was created and last updated. Click a scorecard to open it in the editor.',
          '`Edit` (confirm with `Continue`) changes the linked scorecards and the knowledge-base files. **The name cannot be changed.**',
          '`Deactivate` marks the campaign **Inactive** without losing its history. An inactive campaign shows `Re-activate` and `Delete` in the list instead.',
        ],
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Deleting',
        text:
          'Deactivate first; `Delete` only appears on an inactive campaign, and you must type its name to confirm. Deleting removes the campaign **and permanently deletes its knowledge-base files**. Its scorecards are kept. This cannot be undone.',
      },
    ],
  },

  {
    id: 'scorecards',
    group: 'Set up',
    title: 'Scorecards',
    lede: 'The questions, the criteria, and how the numbers add up. This is the part worth getting right.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Find them under **Quality › Scorecards**. The list shows which campaigns each scorecard is **Used In**, with `Edit` and `Delete` beside it.',
      },
      { type: 'h3', text: 'Three ways to start' },
      {
        type: 'ul',
        items: [
          '`New Scorecard` — build one from scratch in the editor.',
          '`Upload Scorecard` — upload a PDF, Excel, CSV, Word or image file of an existing scorecard. Enter a **Template Name**, choose `Upload & Extract`, and the questions are extracted for you. Check them, reorder, add or remove questions, pick the scoring system, then choose `✓ Confirm & Save`. The scorecard opens in the editor so you can finish it.',
          '`Create New Scorecard` while creating or editing a campaign — see {{Campaigns|#campaigns}}.',
        ],
      },
      { type: 'h3', text: 'Scoring system' },
      {
        type: 'table',
        caption: 'Scoring systems',
        head: ['Option', 'How it scores'],
        rows: [
          [
            '**Yes/No Scoring (v2.0)** (default)',
            'Each question is answered Yes or No. A Quality question earns its full weight when answered with its success answer, and nothing otherwise.',
          ],
          [
            '**Numeric Scoring (v1.0 - Legacy)**',
            'Each question is scored 0–100, then weighted by its **Weight (0-100)**.',
          ],
          [
            '**Compliance-only**',
            'Every question is a pass/fail compliance check. There is no QA score; calls pass or fail on compliance.',
          ],
        ],
      },
      {
        type: 'p',
        text:
          'Switching to Compliance-only turns every question into a Compliance question and drops its QA weighting, so you are asked to confirm with `Convert to Compliance` first. Upload Scorecard offers **Yes/No Scoring** and **Compliance-only**; Legacy is chosen in the editor.',
      },
      { type: 'h3', text: 'Question types' },
      {
        type: 'defs',
        items: [
          {
            term: 'Quality',
            text: 'Scored and weighted. These make up the QA score.',
          },
          {
            term: 'Compliance',
            text:
              'Pass/fail, not weighted. Each has a **Priority** of Low, Medium (default) or High, which you can filter by with **Compliance Priority** on {{Search|#search}} and Campaign Home.',
          },
          {
            term: 'Agent Behavior and Conduct',
            text:
              'Pass/fail, not weighted, and reported separately from compliance (shown as **Conduct** on badges).',
          },
        ],
      },
      { type: 'h3', text: 'Writing a question' },
      {
        type: 'ul',
        items: [
          '**Question Name** is required and must be unique within the scorecard — saving is blocked otherwise.',
          '**Section** is an optional heading, such as Introduction, Pitch or Close.',
          '**Assessment Question** is what the AI answers, for example "Did the agent greet the customer correctly?"',
          '**Expected Criteria** says what good looks like. It is not enforced on save, but a question without it gives the AI little to judge against.',
          'Use the up and down arrows to set the **order**. It is saved with the scorecard and used on the call page and in reports.',
        ],
      },
      { type: 'h3', text: 'Weighting and pass rules (Yes/No scoring)' },
      {
        type: 'ul',
        items: [
          '**QA Weighting (%)** and **Compliance Weighting (%)** are set on Quality questions only. Across all Quality questions, each must total exactly 100% — the running totals below the questions turn green when they do, and saving is blocked until then. A scorecard with no Quality questions has nothing to total.',
          '**Success Answer** says which answer is the good one: `Yes awards points` (default) or `No awards points`, for questions worded in the negative.',
          'Tick **Critical Fail** on any question to fail the whole call when that question is not met.',
        ],
      },
      { type: 'h3', text: 'Questions that do not always apply' },
      {
        type: 'p',
        text:
          'Compliance and Agent Behavior and Conduct questions have a **Not always applicable** box. Tick it and add one or more pre-check steps with `Add step`. Every step must hold for the question to be assessed; if any does not, the question is recorded as **Not Applicable** — not a pass, not a failure, and it cannot fail the call. A ticked box with no steps blocks saving.',
      },
      {
        type: 'note',
        variant: 'danger',
        tag: 'The mistake that quietly breaks scoring',
        text:
          'Write each step as a **condition** that is simply true or false of the call — for example, "An NRV bundle is referred to anywhere in the transcript". Do **not** paste a marking instruction such as "no NRV mentioned = mark N/A". An instruction inverts the check and causes the question to be skipped on exactly the calls it was meant to catch. The editor warns when a step reads like an instruction.',
      },
      { type: 'h3', text: 'Other settings' },
      {
        type: 'ul',
        items: [
          '**Language**: en-ZA (default), en-US, en-GB, zu-ZA (Zulu - South Africa) or Auto Detect.',
          '**Campaign (optional)**: when you save a new version or overwrite, the scorecard is added to this campaign. A brand-new scorecard is not linked this way — link it on the campaign’s Edit page.',
          '**Knowledge Base Files**: reference documents for this scorecard — see {{Knowledge base|#knowledge-base}}.',
        ],
      },
      { type: 'h3', text: 'Saving' },
      {
        type: 'ul',
        items: [
          '`Save New Version` saves a separate scorecard and leaves the original as it was. It needs a **Scorecard Name** no other scorecard uses, so change the name when editing an existing one.',
          '`Overwrite Current Version` replaces the scorecard you are editing. It only appears when editing, and the name must stay the same.',
        ],
      },
      {
        type: 'note',
        variant: 'info',
        tag: 'Before you edit a live scorecard',
        text:
          'Overwriting changes scoring for every campaign that uses the scorecard, and scores from before and after a criteria change are not directly comparable. A new version is added to the campaign you pick alongside the old one — remove the old one on the campaign’s Edit page if it should no longer apply. Knowledge-base files stay with the version they were uploaded to.',
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Deleting',
        text:
          '`Delete` asks you to type the scorecard’s name. It removes the scorecard from every campaign that uses it and permanently deletes its knowledge-base files.',
      },
    ],
  },

  {
    id: 'knowledge-base',
    group: 'Set up',
    title: 'Knowledge base',
    lede: 'Reference material that gives the AI the context a new reviewer would be given.',
    roles: ['sAdm', 'adm'],
    blocks: [
      {
        type: 'p',
        text:
          'Knowledge-base files are product rules, scripts, policy documents or examples of a good call. When the AI scores a call, it draws on the files attached to that call’s campaign and scorecard.',
      },
      {
        type: 'p',
        text: 'There is no separate knowledge-base page. You attach files where they apply:',
      },
      {
        type: 'ul',
        items: [
          '**On a campaign** — `Upload examples` under **Knowledge Base Files** in the campaign editor. See {{Campaigns|#campaigns}}.',
          '**On a scorecard** — the **Knowledge Base Files** section at the bottom of the scorecard editor. See {{Scorecards|#scorecards}}.',
        ],
      },
      {
        type: 'p',
        text: 'Files are uploaded when you save the campaign or scorecard. Attached files are listed on its edit page.',
      },
      {
        type: 'note',
        variant: 'caution',
        tag: 'Removing a file deletes it',
        text:
          'Removing a file from a campaign or scorecard deletes it for good — it is not kept anywhere else. Deleting a campaign or scorecard also deletes all of its files.',
      },
    ],
  },
];

export default setup;
